import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Chip,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import styles from "../styles/landingPage.js";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import {
  BusinessCenterRounded,
  SaveAsRounded,
  SearchRounded,
} from "@mui/icons-material";
import { api } from "common";

const {
  fetchBankFieldRequirements,
  editBankFieldRequirements,
  clearBankFieldError,
  fetchBankUserDetails,
  searchBankUserDetails,
  filterBankUserByType,
  filterBankUserByStatus,
  clearBankUserFilters,
  updateBankUserDetailStatus,
  clearBankUserError,
} = api;

// Styling
const useStyles = makeStyles((theme) => ({
  ...styles,
  root: {
    paddingRight: theme.spacing(20),
    paddingLeft: theme.spacing(20),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    backgroundColor: colors.WHITE,
  },
  header: {
    display: "flex",
    gap: theme.spacing(1),
  },
  card: {
    backgroundColor: colors.WHITE,
    borderRadius: 10,
  },
  topCard: {
    backgroundColor: colors.MAIN_COLOR,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: theme.spacing(3),
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },

  table: {
    minWidth: 650,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  actionButton: {
    margin: theme.spacing(0.5),
  },
  toggleSection: {
    marginBottom: theme.spacing(2),
  },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 48,
  },
  fieldRowHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#D9E7FF",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: theme.spacing(2),
  },
  tableHead: {
    backgroundColor: "#D9E7FF",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: theme.spacing(2),
  },
  tableCell: {
    color: "#4d6ea3",
    fontWeight: 500,
    fontSize: 16,
  },
  gridContainer: {
    borderWidth: 1,
    borderColor: "#D9E7FF",
    borderStyle: "solid",
    alignItems: "center",
  },
  collectedContainer: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "transparent",
  },
  containerField: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#efefef",
    borderRadius: 10,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(4),
  },
  buttonFieldSelected: {
    backgroundColor: colors.WHITE,
    color: colors.MAIN_COLOR,
    flex: 1,
    borderRadius: 4,
    boxShadow: "none",
  },
  buttonFieldUnselected: {
    backgroundColor: "transparent",
    color: colors.BLACK,
    flex: 1,
    borderRadius: 4,
    boxShadow: "none",
  },
  toogleCollected: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    justifyContent: "space-between",
  },
  toogleCollectedItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    justifyContent: "center",
  },
  toggleSwitch: {
    "& .MuiSwitch-track": {
      margin: 0,
      borderRadius: 20,
      backgroundColor: "#afafaf",
    },
    "& .MuiSwitch-root.Mui-checked .MuiSwitch-thumb": {
      backgroundColor: colors.MAIN_COLOR,
    },
    "& .MuiSwitch-root.Mui-checked .MuiSwitch-track": {
      backgroundColor: colors.MAIN_COLOR,
    },
  },
  fieldName: {
    flex: 1,
    fontWeight: 500,
    color: "##4d6ea3",
  },
  fieldDesc: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  enableContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: 20,
  },
  toggleGroup: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flex: 1,
    justifyContent: "flex-end",
  },
  toggleGroupEnable: {
    gap: theme.spacing(2),
    justifyContent: "flex-end",
    alignItems: "center",
    display: "flex",
  },
  headerCell: {
    minWidth: 80,
    textAlign: "center",
    flex: 1,
  },
  toggleCell: {
    minWidth: 80,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    "& .MuiFormControlLabel-root": {
      margin: 0,
      padding: 0,
    },
    "& .MuiSwitch-root": {
      margin: 0,
      padding: 0,
    },
    "& .MuiSwitch-thumb": {
      margin: 0,
    },
    "& .MuiSwitch-track": {
      margin: 0,
      borderRadius: 20,
      backgroundColor: "#afafaf",
    },
    "& .MuiSwitch-root.Mui-checked .MuiSwitch-thumb": {
      backgroundColor: colors.MAIN_COLOR,
    },
    "& .MuiSwitch-root.Mui-checked .MuiSwitch-track": {
      backgroundColor: colors.MAIN_COLOR,
    },
    "& .MuiFormControlLabel-label": {
      display: "none",
    },
    "& .MuiTypography-root": {
      margin: 0,
      padding: 0,
    },
  },
}));

export default function BankAccount() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  // Error boundary state
  const [hasError, setHasError] = useState(false);

  // Redux state selectors
  const bankFieldData = useSelector((state) => state.bankFieldData);
  const bankUserData = useSelector((state) => state.bankUserData);

  // Local state for UI
  const [message, setMessage] = useState({ type: "", text: "" });
  const [detailDialog, setDetailDialog] = useState({ open: false, user: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [isStandardFields, setIsStandardFields] = useState(true);
  const [filterUserType, setFilterUserType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Custom field modal state
  const [addFieldDialog, setAddFieldDialog] = useState({ open: false });
  const [editCustomFieldDialog, setEditCustomFieldDialog] = useState({ 
    open: false, 
    field: null, 
    formData: {
      name: "",
      description: "",
      show: false,
      required: false,
      driver: false,
      user: false,
    }
  });
  const [newFieldData, setNewFieldData] = useState({
    name: "",
    description: "",
    userType: "both",
    required: true
  });
  
  // Custom fields state
  const [customFields, setCustomFields] = useState([]);

  // Extract data from Redux state
  const fieldRequirements = bankFieldData?.data || {};
  const userBankDetails = bankUserData?.filteredData || [];
  const allUserBankDetails = bankUserData?.data || [];
  const loading = bankFieldData?.loading || bankUserData?.loading || false;
  const saving = bankFieldData?.saving || bankUserData?.saving || false;
  const error = bankFieldData?.error || bankUserData?.error;
  const saveSuccess = bankFieldData?.saveSuccess || bankUserData?.saveSuccess;

  // Debug logging
  useEffect(() => {
    console.log('🔍 Bank User Data Debug:');
    console.log('  - All data:', allUserBankDetails.length, 'items');
    console.log('  - Filtered data:', userBankDetails.length, 'items');
    console.log('  - Redux filterUserType:', bankUserData?.filterUserType);
    console.log('  - Redux filterStatus:', bankUserData?.filterStatus);
    console.log('  - Local filterUserType:', filterUserType);
    console.log('  - Local filterStatus:', filterStatus);
    if (allUserBankDetails.length > 0) {
      console.log('  - Sample data fields:', Object.keys(allUserBankDetails[0]));
      console.log('  - Sample userType values:', allUserBankDetails.map(item => item.userType).filter(Boolean));
      console.log('  - Sample status values:', allUserBankDetails.map(item => item.status).filter(Boolean));
    }
  }, [allUserBankDetails, userBankDetails, bankUserData, filterUserType, filterStatus]);
  
  // Extract custom fields from fieldRequirements
  useEffect(() => {
    const extractedCustomFields = [];
    console.log('🔍 Processing fieldRequirements:', fieldRequirements);
    
    Object.entries(fieldRequirements).forEach(([key, config]) => {
      console.log('🔍 Checking field:', key, config);
      if (key.startsWith('custom_') && config) {
        const customField = {
          key: key,
          label: config.name || key,
          desc: config.description || 'Custom field',
          isCustom: true,
          config: config
        };
        extractedCustomFields.push(customField);
        console.log('🔍 Added custom field:', customField);
      }
    });
    
    setCustomFields(extractedCustomFields);
    console.log('🔍 Final custom fields extracted:', extractedCustomFields);
  }, [fieldRequirements]);

  // Enable for users/drivers state from Redux
  const enableForUsers = fieldRequirements?.enableForUsers ?? true;
  const enableForDrivers = fieldRequirements?.enableForDrivers ?? true;

  // Load data on component mount
  useEffect(() => {
    try {
      dispatch(fetchBankFieldRequirements());
      dispatch(fetchBankUserDetails());
    } catch (error) {
      console.error('❌ Error loading bank account data:', error);
      setMessage({
        type: "error",
        text: t("error_loading_bank_account_data"),
      });
    }
  }, [dispatch]);

  // Handle search
  useEffect(() => {
    try {
      dispatch(searchBankUserDetails(searchTerm));
    } catch (error) {
      console.error('❌ Error searching bank user details:', error);
    }
  }, [searchTerm, dispatch]);

  // Handle filters
  useEffect(() => {
    console.log('🔍 Filter changed - UserType:', filterUserType, 'Status:', filterStatus);
  }, [filterUserType, filterStatus]);

  // Force re-filter when data changes
  useEffect(() => {
    if (allUserBankDetails.length > 0) {
      console.log('🔍 Data changed, re-applying filters');
      if (filterUserType !== 'all') {
        dispatch(filterBankUserByType(filterUserType));
      }
      if (filterStatus !== 'all') {
        dispatch(filterBankUserByStatus(filterStatus));
      }
    }
  }, [allUserBankDetails, dispatch]);

  // Sync local state with Redux state
  useEffect(() => {
    const reduxUserType = bankUserData?.filterUserType || '';
    const reduxStatus = bankUserData?.filterStatus || '';
    
    // Convert empty string to 'all' for UI display
    if (reduxUserType === '' && filterUserType !== 'all') {
      setFilterUserType('all');
    } else if (reduxUserType !== '' && filterUserType !== reduxUserType) {
      setFilterUserType(reduxUserType);
    }
    
    if (reduxStatus === '' && filterStatus !== 'all') {
      setFilterStatus('all');
    } else if (reduxStatus !== '' && filterStatus !== reduxStatus) {
      setFilterStatus(reduxStatus);
    }
  }, [bankUserData?.filterUserType, bankUserData?.filterStatus]);

  // Handle success/error messages
  useEffect(() => {
    if (saveSuccess) {
      setMessage({
        type: "success",
        text: t("settings_saved_successfully"),
      });
      // Clear success state after showing message
      setTimeout(() => {
        dispatch(clearBankFieldError());
        dispatch(clearBankUserError());
        setMessage({ type: "", text: "" });
      }, 3000);
    }
  }, [saveSuccess, dispatch]);

  useEffect(() => {
    if (error) {
      setMessage({
        type: "error",
        text: error,
      });
      // Auto clear error after 5 seconds
      setTimeout(() => {
        dispatch(clearBankFieldError());
        dispatch(clearBankUserError());
        setMessage({ type: "", text: "" });
      }, 5000);
    }
  }, [error, dispatch]);

  // Handle message close
  const handleCloseMessage = () => {
    setMessage({ type: "", text: "" });
    dispatch(clearBankFieldError());
    dispatch(clearBankUserError());
  };

  // Custom field modal handlers
  const handleOpenAddFieldDialog = () => {
    setAddFieldDialog({ open: true });
    setNewFieldData({
      name: "",
      description: "",
      userType: "both",
      required: true
    });
  };

  const handleCloseAddFieldDialog = () => {
    setAddFieldDialog({ open: false });
    setNewFieldData({
      name: "",
      description: "",
      userType: "both",
      required: true
    });
  };

  const handleNewFieldInputChange = (field, value) => {
    setNewFieldData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddCustomField = () => {
    if (!newFieldData.name.trim() || !newFieldData.description.trim()) {
      setMessage({
        type: "error",
        text: t("please_fill_all_required_fields")
      });
      return;
    }

    // Generate unique key for the custom field
    const fieldKey = `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Create new field configuration
    const newField = {
      [fieldKey]: {
        show: true,
        required: newFieldData.required,
        user: newFieldData.userType === "both" || newFieldData.userType === "customer",
        driver: newFieldData.userType === "both" || newFieldData.userType === "driver",
        name: newFieldData.name,
        description: newFieldData.description
      }
    };

    console.log('🔧 Creating new custom field:', {
      fieldKey,
      newFieldData,
      newField
    });

    // Update field requirements
    const updatedRequirements = {
      ...fieldRequirements,
      ...newField
    };

    console.log('🔧 Updated requirements:', updatedRequirements);

    // Save to database
    dispatch(editBankFieldRequirements(updatedRequirements));

    // Close modal
    handleCloseAddFieldDialog();

    // Show success message
    setMessage({
      type: "success",
      text: t("custom_field_added_successfully")
    });
  };

  // Global error handler
  useEffect(() => {
    const handleError = (error) => {
      console.error('❌ Unhandled error in BankAccount:', error);
      setHasError(true);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  // Fields
  const bankFields = [
    {
      key: "accountHolderName",
      label: t("name_of_the_holder"),
      desc: t("name_of_the_holder_description"),
    },
    {
      key: "accountNumber",
      label: t("account_number"),
      desc: t("account_number_description"),
    },
    {
      key: "iban",
      label: t("iban"),
      desc: t("iban_description"),
    },
    {
      key: "bankName",
      label: t("bank_name"),
      desc: t("bank_name_description"),
    },
    {
      key: "bankCode",
      label: t("bank_code"),
      desc: t("bank_code_description"),
    },
    {
      key: "branchCode",
      label: t("branch_code"),
      desc: t("branch_code_description"),
    },
    {
      key: "routingNumber",
      label: t("routing_number"),
      desc: t("routing_number_description"),
    },
    {
      key: "swiftCode",
      label: t("swift_code"),
      desc: t("swift_code_description"),
    },
    {
      key: "bankAddress",
      label: t("bank_address"),
      desc: t("bank_address_description"),
    },
    {
      key: "accountType",
      label: t("account_type"),
      desc: t("account_type_description"),
    },
    {
      key: "currency",
      label: t("currency"),
      desc: t("currency_description"),
    },
    {
      key: "country",
      label: t("country"),
      desc: t("country_description"),
    },
    {
      key: "recipientAddress",
      label: t("recipient_address"),
      desc: t("recipient_address_description"),
    },
    {
      key: "emailOrPhone",
      label: t("email_or_phone"),
      desc: t("email_or_phone_description"),
    },
    {
      key: "customAccountNumber",
      label: t("custom_account_number"),
      desc: t("custom_account_number_description"),
    },
  ];

  const handleFieldToggle = (fieldKey, toggleType) => {
    console.log("🔧 Toggle clicked:", { fieldKey, toggleType });
    const updatedRequirements = {
      ...fieldRequirements,
      [fieldKey]: {
        ...fieldRequirements[fieldKey],
        [toggleType]: !fieldRequirements[fieldKey][toggleType],
      },
    };
    console.log("🔧 New state:", updatedRequirements);
    dispatch(editBankFieldRequirements(updatedRequirements));
  };

  const handleEditCustomField = (field) => {
    console.log("✏️ Edit custom field:", field);
    // Open edit dialog for custom field
    setEditCustomFieldDialog({
      open: true,
      field: field,
      formData: {
        name: field.label,
        description: field.desc,
        show: fieldRequirements[field.key]?.show || false,
        required: fieldRequirements[field.key]?.required || false,
        driver: fieldRequirements[field.key]?.driver || false,
        user: fieldRequirements[field.key]?.user || false,
      }
    });
  };

  const handleSaveEditCustomField = () => {
    const { field, formData } = editCustomFieldDialog;
    console.log("💾 Saving edited custom field:", { field, formData });
    
    // Update the field requirements with new data
    const updatedRequirements = {
      ...fieldRequirements,
      [field.key]: {
        ...fieldRequirements[field.key],
        name: formData.name,
        description: formData.description,
        show: formData.show,
        required: formData.required,
        driver: formData.driver,
        user: formData.user,
      },
    };
    
    // Save to Redux
    dispatch(editBankFieldRequirements(updatedRequirements));
    
    // Close dialog
    setEditCustomFieldDialog({ ...editCustomFieldDialog, open: false });
    
    // Show success message
    setMessage({
      type: "success",
      text: t("custom_field_updated_successfully"),
    });
  };

  const handleSaveRequirements = () => {
    console.log("Saving field requirements:", fieldRequirements);
    dispatch(editBankFieldRequirements(fieldRequirements));
  };

  const handleEnableForUsers = () => {
    const updatedRequirements = {
      ...fieldRequirements,
      enableForUsers: !enableForUsers,
    };
    dispatch(editBankFieldRequirements(updatedRequirements));
  };

  const handleEnableForDrivers = () => {
    const updatedRequirements = {
      ...fieldRequirements,
      enableForDrivers: !enableForDrivers,
    };
    dispatch(editBankFieldRequirements(updatedRequirements));
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleUserTypeFilterChange = (userType) => {
    console.log('🔍 UserType filter changed to:', userType);
    setFilterUserType(userType);
    if (userType === "all") {
      dispatch(filterBankUserByType(""));
    } else {
      dispatch(filterBankUserByType(userType));
    }
  };

  const handleStatusFilterChange = (status) => {
    console.log('🔍 Status filter changed to:', status);
    setFilterStatus(status);
    if (status === "all") {
      dispatch(filterBankUserByStatus(""));
    } else {
      dispatch(filterBankUserByStatus(status));
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterUserType("all");
    setFilterStatus("all");
    dispatch(clearBankUserFilters());
    console.log('🔍 Clear filters called');
  };

  const handleStatusChange = (userId, newStatus) => {
    dispatch(updateBankUserDetailStatus(userId, newStatus));
  };

  const handleViewDetail = (user) => {
    setDetailDialog({ open: true, user: user });
  };

  const handleCloseDetailDialog = () => {
    setDetailDialog({ open: false, user: null });
  };

  const getStatusChip = (status) => {
    const color = status === "active" ? "success" : "warning";
    const label = status === "active" ? t("active") : t("pending");
    return (
      <Chip label={label} color={color} size="small" className={classes.chip} />
    );
  };

  const getUserTypeChip = (userType) => {
    const color = userType === "driver" ? "primary" : "secondary";
    const label = userType === "driver" ? t("driver") : t("user");
    return (
      <Chip label={label} color={color} size="small" className={classes.chip} />
    );
  };

  // Show error state if there's a critical error
  if (hasError) {
    return (
      <div className={classes.root}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="error">
              <Typography variant="h6">Something went wrong</Typography>
              <Typography variant="body2">
                There was an error loading the bank account management page. Please refresh the page or contact support.
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => window.location.reload()} 
                style={{ marginTop: 16 }}
              >
                Refresh Page
              </Button>
            </Alert>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <div className={classes.header}>
            <BusinessCenterRounded fontSize="large" />
            <Typography variant="h4" gutterBottom fontWeight={600}>
              {t("bank_account_management")}
            </Typography>
          </div>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            {t("configure_bank_account_fields_description")}
          </Typography>
        </Grid>

        {message.text && (
          <Grid item xs={12}>
            <Alert severity={message.type} onClose={handleCloseMessage}>
              {message.text}
            </Alert>
          </Grid>
        )}

        {/* Field Requirements Section */}
        <Grid item xs={12} borderTop={0}>
          <Card className={classes.card}>
            <Card className={classes.topCard}>
              <Typography variant="h6" color={colors.WHITE}>
                {t("field_requirements_configuration")}
              </Typography>
              <Typography variant="body1" color={colors.WHITE}>
                {t(
                  "select_which_bank_account_fields_you_want_to_enable_and_for_which_type_of_users"
                )}
              </Typography>
            </Card>
            <div className={classes.enableContainer}>
              <Box>
                <Typography variant="h6">{t("enable_for")}:</Typography>
                <Typography variant="body1">
                  {t(
                    "select_which_type_of_users_bank_details_will_be_collected"
                  )}
                </Typography>
              </Box>

              <Box className={classes.toggleGroupEnable}>
                <Box className={classes.toggleCell}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableForUsers}
                        onChange={handleEnableForUsers}
                        size="small"
                      />
                    }
                    label=""
                  />
                  <Typography variant="body1" style={{ marginLeft: 10 }}>
                    {t("users")}
                  </Typography>
                </Box>
                <Box className={classes.toggleCell}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableForDrivers}
                        onChange={handleEnableForDrivers}
                        size="small"
                      />
                    }
                    label=""
                  />
                  <Typography variant="body1" style={{ marginLeft: 10 }}>
                    {t("driver")}
                  </Typography>
                </Box>
              </Box>
            </div>

            <CardContent>
              <div className={classes.containerField}>
                <Button
                  variant="contained"
                  color="primary"
                  className={
                    isStandardFields === true
                      ? classes.buttonFieldSelected
                      : classes.buttonFieldUnselected
                  }
                  onClick={() => setIsStandardFields(true)}
                >
                  {t("standard_fields")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className={
                    isStandardFields === false
                      ? classes.buttonFieldSelected
                      : classes.buttonFieldUnselected
                  }
                  onClick={() => setIsStandardFields(false)}
                >
                  {t("custom_fields")}
                </Button>
              </div>
              {isStandardFields === true ? (
                <div>
                  <Grid container spacing={4} style={{ borderWidth: 1 }}>
                    <Grid item xs={12}>
                      <Box className={classes.fieldRowHeader}>
                        <Typography
                          className={classes.fieldName}
                          variant="h6"
                          color={"#4d6ea3"}
                        >
                          {t("field_name")}
                        </Typography>
                        <Box className={classes.toggleGroup}>
                          <Box className={classes.toggleCell}>
                            <Typography
                              variant="body1"
                              className={classes.headerCell}
                              color={"#4d6ea3"}
                              fontWeight={600}
                            >
                              {t("show")}
                            </Typography>
                          </Box>
                          <Box className={classes.toggleCell}>
                            <Typography
                              variant="body1"
                              className={classes.headerCell}
                              color={"#4d6ea3"}
                              fontWeight={600}
                            >
                              {t("required")}
                            </Typography>
                          </Box>
                          <Box className={classes.toggleCell}>
                            <Typography
                              variant="body1"
                              className={classes.headerCell}
                              color={"#4d6ea3"}
                              fontWeight={600}
                            >
                              {t("driver")}
                            </Typography>
                          </Box>
                          <Box className={classes.toggleCell}>
                            <Typography
                              variant="body1"
                              className={classes.headerCell}
                              color={"#4d6ea3"}
                              fontWeight={600}
                            >
                              {t("user")}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      {bankFields.map((field) => (
                        <Grid
                          item
                          xs={12}
                          key={field.key}
                          className={classes.gridContainer}
                        >
                          <Box className={classes.fieldRow}>
                            <Box style={{ flex: 1 }}>
                              <Typography
                                className={classes.fieldName}
                                variant="h6"
                              >
                                {field.label}
                              </Typography>
                              <Typography className={classes.fieldDesc}>
                                {field.desc}
                              </Typography>
                            </Box>
                            <Box className={classes.toggleGroup}>
                              <Box className={classes.toggleCell}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={
                                        fieldRequirements[field.key]?.show ||
                                        false
                                      }
                                      onChange={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        console.log(
                                          "🔧 Switch clicked for:",
                                          field.key,
                                          "show",
                                          "checked:",
                                          event.target.checked
                                        );
                                        handleFieldToggle(field.key, "show");
                                      }}
                                      size="small"
                                    />
                                  }
                                  label=""
                                />
                              </Box>
                              <Box className={classes.toggleCell}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={
                                        fieldRequirements[field.key]
                                          ?.required || false
                                      }
                                      onChange={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        console.log(
                                          "🔧 Switch clicked for:",
                                          field.key,
                                          "required",
                                          "checked:",
                                          event.target.checked
                                        );
                                        handleFieldToggle(
                                          field.key,
                                          "required"
                                        );
                                      }}
                                      size="small"
                                    />
                                  }
                                  label=""
                                />
                              </Box>
                              <Box className={classes.toggleCell}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={
                                        fieldRequirements[field.key]?.driver ||
                                        false
                                      }
                                      onChange={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        console.log(
                                          "🔧 Switch clicked for:",
                                          field.key,
                                          "driver",
                                          "checked:",
                                          event.target.checked
                                        );
                                        handleFieldToggle(field.key, "driver");
                                      }}
                                      size="small"
                                    />
                                  }
                                  label=""
                                />
                              </Box>
                              <Box className={classes.toggleCell}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={
                                        fieldRequirements[field.key]?.user ||
                                        false
                                      }
                                      onChange={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        console.log(
                                          "🔧 Switch clicked for:",
                                          field.key,
                                          "user",
                                          "checked:",
                                          event.target.checked
                                        );
                                        handleFieldToggle(field.key, "user");
                                      }}
                                      size="small"
                                    />
                                  }
                                  label=""
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                  <Button
                    variant="contained"
                    onClick={handleSaveRequirements}
                    disabled={saving}
                    style={{
                      color: colors.WHITE,
                      backgroundColor: colors.MAIN_COLOR,
                      marginTop: 10,
                      borderRadius: 6,
                      borderWidth: 1,
                      boxShadow: "none",
                      gap: 4,
                      fontSize: 14,
                    }}
                  >
                    <SaveAsRounded fontSize="small" />
                    {saving ? t("saving...") : t("save_setting")}
                  </Button>
                </div>
              ) : (
                <div>
                  <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography variant="h6">{t("custom_fields")}</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleOpenAddFieldDialog}
                      style={{
                        backgroundColor: colors.MAIN_COLOR,
                        color: colors.WHITE,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      {t("add_new_field")}
                    </Button>
                  </Box>
                  
                  {/* Custom Fields List */}
                  {customFields.length > 0 ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box className={classes.fieldRow}>
                          <Box style={{ flex: 1 }}>
                            <Typography
                              className={classes.fieldName}
                              variant="h6"
                              style={{ color: colors.MAIN_COLOR }}
                            >
                              {t("field_name")}
                            </Typography>
                          </Box>
                          <Box className={classes.toggleGroup}>
                            <Box className={classes.toggleCell}>
                              <Typography
                                variant="body2"
                                className={classes.headerCell}
                                color={"#4d6ea3"}
                                fontWeight={600}
                              >
                                {t("show")}
                              </Typography>
                            </Box>
                            <Box className={classes.toggleCell}>
                              <Typography
                                variant="body2"
                                className={classes.headerCell}
                                color={"#4d6ea3"}
                                fontWeight={600}
                              >
                                {t("required")}
                              </Typography>
                            </Box>
                            <Box className={classes.toggleCell}>
                              <Typography
                                variant="body2"
                                className={classes.headerCell}
                                color={"#4d6ea3"}
                                fontWeight={600}
                              >
                                {t("driver")}
                              </Typography>
                            </Box>
                            <Box className={classes.toggleCell}>
                              <Typography
                                variant="body2"
                                className={classes.headerCell}
                                color={"#4d6ea3"}
                                fontWeight={600}
                              >
                                {t("user")}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                      
                      {customFields.map((field) => (
                        <Grid
                          item
                          xs={12}
                          key={field.key}
                          className={classes.gridContainer}
                        >
                          <Box className={classes.fieldRow}>
                            <Box style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box>
                                <Typography
                                  className={classes.fieldName}
                                  variant="h6"
                                >
                                  {field.label}
                                </Typography>
                                <Typography className={classes.fieldDesc}>
                                  {field.desc}
                                </Typography>
                              </Box>
                              <Box style={{ marginLeft: 16 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCustomField(field)}
                                  style={{ color: colors.MAIN_COLOR }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Box className={classes.toggleGroup}>
                              <Box className={classes.toggleCell}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={
                                        fieldRequirements[field.key]?.show ||
                                        false
                                      }
                                      onChange={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleFieldToggle(field.key, "show");
                                      }}
                                      size="small"
                                    />
                                  }
                                  label=""
                                />
                              </Box>
                              <Box className={classes.toggleCell}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={
                                        fieldRequirements[field.key]
                                          ?.required || false
                                      }
                                      onChange={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleFieldToggle(
                                          field.key,
                                          "required"
                                        );
                                      }}
                                      size="small"
                                    />
                                  }
                                  label=""
                                />
                              </Box>
                              <Box className={classes.toggleCell}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={
                                        fieldRequirements[field.key]?.driver ||
                                        false
                                      }
                                      onChange={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleFieldToggle(field.key, "driver");
                                      }}
                                      size="small"
                                    />
                                  }
                                  label=""
                                />
                              </Box>
                              <Box className={classes.toggleCell}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={
                                        fieldRequirements[field.key]?.user ||
                                        false
                                      }
                                      onChange={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleFieldToggle(field.key, "user");
                                      }}
                                      size="small"
                                    />
                                  }
                                  label=""
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box style={{ textAlign: 'center', padding: 20 }}>
                      <Typography variant="body1" color="textSecondary">
                        {t("no_custom_fields_yet")}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        {t("click_add_new_field_to_create")}
                      </Typography>
                    </Box>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* User Bank Details Section */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <Card className={classes.topCard}>
              <Box>
                <Typography variant="h5" color={colors.WHITE} fontWeight={600}>
                  {t("data_collected")}
                </Typography>
                <Typography variant="body1" color={colors.WHITE}>
                  {t("view_the_data_collected_from_users_and_drivers")}
                </Typography>
              </Box>

                              <Box
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 20,
                    marginTop: 24,
                    flexWrap: "wrap",
                    rowGap: 16,
                    padding: "20px",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Typography 
                    variant="body2" 
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 600,
                      fontSize: '14px',
                      marginLeft: 4,
                    }}
                  >
                    {t("search")}
                  </Typography>
                  <TextField
                    placeholder={t("search_by_name_and_bank")}
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{
                      minWidth: 300,
                      maxWidth: 300,
                      backgroundColor: colors.WHITE,
                      borderRadius: 8,
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Typography 
                    variant="body2" 
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 600,
                      fontSize: '14px',
                      marginLeft: 4,
                    }}
                  >
                    {t("user_type")}
                  </Typography>
                  <FormControl
                    size="small"
                    style={{
                      minWidth: 160,
                      maxWidth: 160,
                      backgroundColor: colors.WHITE,
                      borderRadius: 8,
                      flexShrink: 0,
                    }}
                  >
                    <Select
                      value={filterUserType}
                      onChange={(e) => handleUserTypeFilterChange(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">{t("all")}</MenuItem>
                      <MenuItem value="driver">{t("driver")}</MenuItem>
                      <MenuItem value="user">{t("user")}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Typography 
                    variant="body2" 
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 600,
                      fontSize: '14px',
                      marginLeft: 4,
                    }}
                  >
                    {t("status")}
                  </Typography>
                  <FormControl
                    size="small"
                    style={{
                      minWidth: 160,
                      maxWidth: 160,
                      backgroundColor: colors.WHITE,
                      borderRadius: 8,
                      flexShrink: 0,
                    }}
                  >
                    <Select
                      value={filterStatus}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">{t("all")}</MenuItem>
                      <MenuItem value="active">{t("active")}</MenuItem>
                      <MenuItem value="pending">{t("pending")}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Typography 
                    variant="body2" 
                    style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 600,
                      fontSize: '14px',
                      marginLeft: 4,
                    }}
                  >
                    Actions
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleClearFilters}
                    style={{
                      backgroundColor: colors.WHITE,
                      color: colors.MAIN_COLOR,
                      boxShadow: "none",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      borderRadius: 8,
                      padding: "8px 16px",
                    }}
                  >
                    {t("clear_filters")}
                  </Button>
                </Box>
              </Box>
            </Card>
            <CardContent>
              <TableContainer component={Paper}>
                <Table className={classes.table}>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableCell}>
                        {t("user_type")}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {t("user_name")}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {t("bank_name")}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {t("account_number")}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {t("country")}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {t("status")}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {t("actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      // Loading skeleton rows
                      Array.from({ length: 3 }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell>
                            <Skeleton
                              variant="rectangular"
                              width={80}
                              height={24}
                            />
                          </TableCell>
                          <TableCell>
                            <Skeleton variant="text" width={120} />
                          </TableCell>
                          <TableCell>
                            <Skeleton variant="text" width={150} />
                          </TableCell>
                          <TableCell>
                            <Skeleton variant="text" width={100} />
                          </TableCell>
                          <TableCell>
                            <Skeleton variant="text" width={80} />
                          </TableCell>
                          <TableCell>
                            <Skeleton
                              variant="rectangular"
                              width={70}
                              height={24}
                            />
                          </TableCell>
                          <TableCell>
                            <Skeleton
                              variant="circular"
                              width={32}
                              height={32}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : userBankDetails.length === 0 ? (
                      // Empty state
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Box py={4}>
                            <Typography variant="h6" color="textSecondary">
                              {searchTerm
                                ? t("no_results_found")
                                : t("no_bank_details_available")}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {searchTerm
                                ? t("try_different_search_terms")
                                : t("bank_details_will_appear_here")}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      // Data rows
                      userBankDetails.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            {getUserTypeChip(user.userType)}
                          </TableCell>
                          <TableCell>{user.userName}</TableCell>
                          <TableCell>{user.bankName}</TableCell>
                          <TableCell>{user.accountNumber}</TableCell>
                          <TableCell>{user.country}</TableCell>
                          <TableCell>{getStatusChip(user.status)}</TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    gutterBottom
                                  >
                                    {t("user_details")}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>{t("user_name")}:</strong>{" "}
                                    {user.userName}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>{t("bank_name")}:</strong>{" "}
                                    {user.bankName}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>{t("account_number")}:</strong>{" "}
                                    {user.accountNumber}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>{t("country")}:</strong>{" "}
                                    {user.country}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>{t("status")}:</strong>{" "}
                                    {user.status}
                                  </Typography>
                                </Box>
                              }
                              arrow
                              placement="left"
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetail(user)}
                                className={classes.actionButton}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={handleCloseDetailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {t("user_bank_details")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {detailDialog.user && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card variant="outlined" style={{ padding: 20 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          {t("user_type")}
                        </Typography>
                        <Typography variant="h6" style={{ marginTop: 4 }}>
                          {detailDialog.user.userType === "driver"
                            ? t("driver")
                            : t("user")}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          {t("status")}
                        </Typography>
                        <Box style={{ marginTop: 4 }}>
                          {getStatusChip(detailDialog.user.status)}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider style={{ margin: "16px 0" }} />
                      </Grid>
                      {/* Dynamic Bank Details - Show all filled fields */}
                      {Object.entries(detailDialog.user).map(([key, value]) => {
                        // Skip system fields and empty values
                        if (
                          ['id', 'userId', 'userType', 'status', 'createdAt', 'updatedAt', 'createdBy', 'created_by'].includes(key) ||
                          !value ||
                          value === ''
                        ) {
                          return null;
                        }

                        // Get field configuration
                        const fieldConfig = fieldRequirements[key];
                        const userType = detailDialog.user.userType || 'user';
                        
                        // For admin view mode, show all fields regardless of configuration
                        // This ensures admins can see everything when they click the "eye" icon

                        // Get field label
                        let fieldLabel = key;
                        if (fieldConfig?.name) {
                          fieldLabel = fieldConfig.name;
                        } else {
                          // Map standard field keys to labels
                          const fieldLabels = {
                            accountHolderName: t('account_holder_name'),
                            bankName: t('bank_name'),
                            bankCode: t('bank_code'),
                            accountNumber: t('account_number'),
                            iban: t('iban'),
                            routingNumber: t('routing_number'),
                            swiftCode: t('swift_code'),
                            bankAddress: t('bank_address'),
                            accountType: t('account_type'),
                            currency: t('currency'),
                            country: t('country'),
                            recipientAddress: t('recipient_address'),
                            emailOrPhone: t('email_or_phone'),
                            userName: t('user_name'),
                          };
                          fieldLabel = fieldLabels[key] || key;
                        }

                        // Display all data without masking
                        let displayValue = value;

                        return (
                          <Grid item xs={12} key={key}>
                            <Typography variant="body2" color="textSecondary">
                              {fieldLabel}
                            </Typography>
                            <Typography variant="h6" style={{ marginTop: 4 }}>
                              {displayValue}
                            </Typography>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDetailDialog}
            variant="contained"
            style={{ backgroundColor: colors.MAIN_COLOR }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Field Dialog */}
      <Dialog
        open={addFieldDialog.open}
        onClose={handleCloseAddFieldDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {t("add_new_field")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box style={{ padding: '8px 0' }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("name") + " *"}
                  value={newFieldData.name}
                  onChange={(e) => handleNewFieldInputChange('name', e.target.value)}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("description") + " *"}
                  value={newFieldData.description}
                  onChange={(e) => handleNewFieldInputChange('description', e.target.value)}
                  variant="outlined"
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t("for")}</InputLabel>
                  <Select
                    value={newFieldData.userType}
                    onChange={(e) => handleNewFieldInputChange('userType', e.target.value)}
                    label={t("for")}
                  >
                    <MenuItem value="both">{t("both")}</MenuItem>
                    <MenuItem value="customer">{t("customer")}</MenuItem>
                    <MenuItem value="driver">{t("driver")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newFieldData.required}
                      onChange={(e) => handleNewFieldInputChange('required', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t("mandatory")}
                  style={{ margin: 0 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAddFieldDialog}
            variant="outlined"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleAddCustomField}
            variant="contained"
            style={{ backgroundColor: colors.MAIN_COLOR }}
          >
            {t("add_field")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Custom Field Dialog */}
      <Dialog
        open={editCustomFieldDialog.open}
        onClose={() => setEditCustomFieldDialog({ ...editCustomFieldDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {t("edit_custom_field")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box style={{ padding: '8px 0' }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("name") + " *"}
                  value={editCustomFieldDialog.formData.name}
                  onChange={(e) => setEditCustomFieldDialog({
                    ...editCustomFieldDialog,
                    formData: { ...editCustomFieldDialog.formData, name: e.target.value }
                  })}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("description") + " *"}
                  value={editCustomFieldDialog.formData.description}
                  onChange={(e) => setEditCustomFieldDialog({
                    ...editCustomFieldDialog,
                    formData: { ...editCustomFieldDialog.formData, description: e.target.value }
                  })}
                  variant="outlined"
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editCustomFieldDialog.formData.show}
                      onChange={(e) => setEditCustomFieldDialog({
                        ...editCustomFieldDialog,
                        formData: { ...editCustomFieldDialog.formData, show: e.target.checked }
                      })}
                      color="primary"
                    />
                  }
                  label={t("show")}
                  style={{ margin: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editCustomFieldDialog.formData.required}
                      onChange={(e) => setEditCustomFieldDialog({
                        ...editCustomFieldDialog,
                        formData: { ...editCustomFieldDialog.formData, required: e.target.checked }
                      })}
                      color="primary"
                    />
                  }
                  label={t("required")}
                  style={{ margin: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editCustomFieldDialog.formData.driver}
                      onChange={(e) => setEditCustomFieldDialog({
                        ...editCustomFieldDialog,
                        formData: { ...editCustomFieldDialog.formData, driver: e.target.checked }
                      })}
                      color="primary"
                    />
                  }
                  label={t("driver")}
                  style={{ margin: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editCustomFieldDialog.formData.user}
                      onChange={(e) => setEditCustomFieldDialog({
                        ...editCustomFieldDialog,
                        formData: { ...editCustomFieldDialog.formData, user: e.target.checked }
                      })}
                      color="primary"
                    />
                  }
                  label={t("user")}
                  style={{ margin: 0 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditCustomFieldDialog({ ...editCustomFieldDialog, open: false })}
            variant="outlined"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSaveEditCustomField}
            variant="contained"
            style={{ backgroundColor: colors.MAIN_COLOR }}
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
